/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Spinner,
  Card,
  Badge,
  Dropdown,
  ProgressBar,
} from 'react-bootstrap';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import logo from '../assets/LogoNavbar.png';
import type {
  CompanyProfile,
  Operations,
  RestrictedFields,
  SupplierCertification,
  SupplierPolicy,
} from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

import './profile.scss';
import {
  getBarColor,
  getTotalScorePercentage,
  sanctionScreeningStatusMap,
  tierOneSuppliersAssessmentMap,
  tierTwoSuppliersAssessmentMap,
} from '../util/util';

const PDF_RENDER_SCALE = 1.25;
const PDF_IMAGE_QUALITY = 0.72;
const csvHeaders = ['section', 'item', 'key', 'value'];

type CsvValue = string | number | boolean | null | undefined;
type CsvRow = [string, string, string, CsvValue];

// const getScoreVariant = (score: number) => {
//   if (score >= 80) return 'success';
//   if (score >= 50) return 'warning';
//   return 'danger';
// };

const getCsvCellValue = (value: CsvValue) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const escapeCsvValue = (value: CsvValue) => {
  const normalizedValue = getCsvCellValue(value).replace(/"/g, '""');
  return `"${normalizedValue}"`;
};

const buildProfileCsv = ({
  profile,
  operations,
  countries,
  allCountries,
  policies,
  certifications,
  restrictedFields,
  publishedReports,
  industryOptions,
}: {
  profile: CompanyProfile;
  operations: Operations | null;
  countries: any[];
  allCountries: any[];
  policies: (SupplierPolicy & { signed_url?: string | null })[];
  certifications: (SupplierCertification & { signed_url?: string | null })[];
  restrictedFields: RestrictedFields | null;
  publishedReports: any[];
  industryOptions: any[];
}) => {
  const rows: CsvRow[] = [];
  const industryLabel =
    industryOptions.find((option) => option.id === profile.industry_id)
      ?.label || '';
  const headquartersCountry =
    allCountries.find(
      (country) => country.code === profile.headquarters_location,
    )?.name || profile.headquarters_location;

  rows.push(['profile', 'company_name', 'company_name', profile.company_name]);
  rows.push([
    'profile',
    'registration_number',
    'registration_number',
    profile.registration_number,
  ]);
  rows.push(['profile', 'industry', 'industry', industryLabel]);
  rows.push(['profile', 'website', 'website', profile.website]);
  rows.push(['profile', 'description', 'description', profile.description]);
  rows.push([
    'profile',
    'headquarters_location',
    'headquarters_location',
    headquartersCountry,
  ]);
  rows.push(['profile', 'founded_year', 'founded_year', profile.founded_year]);
  rows.push([
    'profile',
    'contact_email',
    'contact_email',
    profile.contact_email,
  ]);
  rows.push(['profile', 'linkedin_url', 'linkedin_url', profile.linkedin_url]);
  rows.push(['profile', 'duns_number', 'duns_number', profile.duns_number]);
  rows.push([
    'profile',
    'display_assessment_results',
    'display_assessment_results',
    profile.display_assesment_results,
  ]);
  rows.push(['profile', 'updated_at', 'updated_at', profile.updated_at]);
  rows.push([
    'profile',
    'tier_1_visibility_level',
    'tier_1_visibility_level',
    profile.tier_1_visibility_level,
  ]);
  rows.push([
    'profile',
    'tier_2_visibility_level',
    'tier_2_visibility_level',
    profile.tier_2_visibility_level,
  ]);
  rows.push([
    'profile',
    'raw_material_origins',
    'raw_material_origins',
    profile.raw_material_origins?.join(', '),
  ]);

  rows.push([
    'operations',
    'employee_count',
    'employee_count',
    operations?.employee_count,
  ]);
  rows.push([
    'operations',
    'revenue_range',
    'revenue_range',
    operations?.revenue_range,
  ]);
  rows.push([
    'operations',
    'ownership_type',
    'ownership_type',
    operations?.ownership_type,
  ]);

  countries.forEach((country, index) => {
    rows.push([
      'operations_country',
      `country_${index + 1}`,
      'name',
      country.countries?.name,
    ]);
  });

  rows.push([
    'financial',
    'is_financials_public',
    'is_financials_public',
    restrictedFields?.is_financials_public,
  ]);
  rows.push([
    'financial',
    'ubo_details',
    'ubo_details',
    restrictedFields?.ubo_details,
  ]);
  rows.push([
    'financial',
    'sanction_screening_status',
    'sanction_screening_status',
    restrictedFields?.sanction_screening_status,
  ]);
  rows.push([
    'financial',
    'credit_score_value',
    'credit_score_value',
    restrictedFields?.credit_score_value,
  ]);
  rows.push([
    'financial',
    'credit_score_provider',
    'credit_score_provider',
    restrictedFields?.credit_score_provider,
  ]);
  rows.push([
    'financial',
    'credit_report_url',
    'credit_report_url',
    restrictedFields?.credit_report_url,
  ]);
  rows.push([
    'financial',
    'last_financial_audit_date',
    'last_financial_audit_date',
    restrictedFields?.last_financial_audit_date,
  ]);
  rows.push([
    'financial',
    'annual_revenue_range',
    'annual_revenue_range',
    restrictedFields?.annual_revenue_range,
  ]);

  policies.forEach((policy, index) => {
    rows.push([
      'policy',
      `policy_${index + 1}`,
      'policy_type',
      policy.policy_type,
    ]);
    rows.push([
      'policy',
      `policy_${index + 1}`,
      'policy_name',
      policy.policy_name,
    ]);
    rows.push(['policy', `policy_${index + 1}`, 'file_url', policy.file_url]);
    rows.push([
      'policy',
      `policy_${index + 1}`,
      'signed_url',
      policy.signed_url,
    ]);
    rows.push(['policy', `policy_${index + 1}`, 'is_public', policy.is_public]);
  });

  certifications.forEach((certification, index) => {
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'certificate_name',
      certification.certificate_name,
    ]);
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'issuing_body',
      certification.issuing_body,
    ]);
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'issue_date',
      certification.issue_date,
    ]);
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'valid_until',
      certification.valid_until,
    ]);
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'certificate_file_url',
      certification.certificate_file_url,
    ]);
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'signed_url',
      certification.signed_url,
    ]);
    rows.push([
      'certification',
      `certification_${index + 1}`,
      'is_verified',
      certification.is_verified,
    ]);
  });

  publishedReports.forEach((publishedReport, index) => {
    rows.push([
      'published_report',
      `report_${index + 1}`,
      'id',
      publishedReport.id,
    ]);
    rows.push([
      'published_report',
      `report_${index + 1}`,
      'assessment_id',
      publishedReport.assessment_id,
    ]);
    rows.push([
      'published_report',
      `report_${index + 1}`,
      'status',
      publishedReport.status,
    ]);
    rows.push([
      'published_report',
      `report_${index + 1}`,
      'published_at',
      publishedReport.published_at,
    ]);
    rows.push([
      'published_report',
      `report_${index + 1}`,
      'created_at',
      publishedReport.created_at,
    ]);
  });

  return [csvHeaders, ...rows]
    .map((row) => row.map(escapeCsvValue).join(','))
    .join('\n');
};

const SupplierProfilePublic: React.FC = () => {
  const { profileId } = useParams();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [operations, setOperations] = useState<Operations | null>(null);
  const [countries, setCountries] = useState<any[]>([]);
  const [allCountries, setAllCountries] = useState<any[]>([]);
  const [policies, setPolicies] = useState<
    (SupplierPolicy & { signed_url?: string | null })[]
  >([]);
  const [certifications, setCertifications] = useState<
    (SupplierCertification & { signed_url?: string | null })[]
  >([]);
  const [restrictedFields, setRestrictedFields] =
    useState<RestrictedFields | null>(null);
  const [publishedReports, setPublishedReports] = useState<any[]>([]);
  const [industryOptions, setIndustryOptions] = useState<any[]>([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const profileContentRef = useRef<HTMLDivElement>(null);

  const documentBucket = useMemo(() => 'supplier-profile-assets', []);

  useEffect(() => {
    const defaultTitle = 'SupplyPassport App';

    if (profile?.company_name) {
      document.title = `${profile.company_name} | SupplyPassport App`;
      return;
    }

    document.title = defaultTitle;
  }, [profile?.company_name]);

  const resolveDocUrl = async (path: string | null | undefined) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;

    const { data, error } = await supabase.storage
      .from(documentBucket)
      .createSignedUrl(path, 60 * 15);

    if (error) {
      captureSupabaseError(error, { op: 'doc.signed-url' });
      return null;
    }
    return data?.signedUrl ?? null;
  };

  useEffect(() => {
    if (!profileId) return;

    const loadAllData = async () => {
      setLoading(true);
      try {
        const { data: profileData, error: profileErr } = await supabase
          .from('supplier_company_profiles')
          .select('*')
          .eq('id', profileId)
          .single();

        if (profileErr || !profileData) throw profileErr;
        if (profileData?.profile_status === 'draft') {
          throw new Error('Profile not found');
        }
        setProfile(profileData);

        const sId = profileData.supplier_id;

        const [
          ops,
          counts,
          allC,
          pols,
          certs,
          pubReports,
          industryOptions,
          restrictedRes,
        ] = await Promise.all([
          supabase
            .from('supplier_company_operations')
            .select('*')
            .eq('supplier_id', sId)
            .single(),
          supabase
            .from('supplier_operation_countries')
            .select('country_id, countries(name)')
            .eq('supplier_id', sId),
          supabase.from('countries').select('id, name, code'),
          supabase
            .from('supplier_policies')
            .select('*')
            .eq('supplier_id', sId)
            .eq('is_public', true)
            .order('created_at', { ascending: false }),
          supabase
            .from('supplier_certifications')
            .select('*')
            .eq('supplier_id', sId)
            .order('created_at', { ascending: false }),
          supabase
            .from('published_reports')
            .select('*')
            .eq('supplier_id', sId)
            .eq('status', 'published')
            .order('created_at', { ascending: false }),
          supabase.from('industry_options').select('*'),
          supabase
            .from('supplier_company_profile_restricted_fields')
            .select('*')
            .eq('profile_id', profileId)
            .eq('is_financials_public', true)
            .maybeSingle(),
        ]);

        if (ops.data) setOperations(ops.data);
        if (counts.data) setCountries(counts.data);
        if (allC.data) setAllCountries(allC.data);
        if (pubReports.data) setPublishedReports(pubReports.data);
        if (industryOptions.data) setIndustryOptions(industryOptions.data);
        if (restrictedRes.data) setRestrictedFields(restrictedRes.data);

        const enrichedPolicies = await Promise.all(
          (pols.data || []).map(async (p) => ({
            ...p,
            signed_url: await resolveDocUrl(p.file_url),
          })),
        );
        const enrichedCerts = await Promise.all(
          (certs.data || []).map(async (c) => ({
            ...c,
            signed_url: await resolveDocUrl(c.certificate_file_url),
          })),
        );

        setPolicies(enrichedPolicies);
        setCertifications(enrichedCerts);
      } catch (err) {
        console.error('Profile load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [profileId, documentBucket]);

  const showFinancial = Boolean(restrictedFields?.is_financials_public);
  const headquartersName =
    allCountries.find((c) => c.code === profile?.headquarters_location)?.name ||
    profile?.headquarters_location ||
    '—';
  const industryLabel =
    industryOptions.find((opt) => opt.id === profile?.industry_id)?.label ||
    'Industry not specified';

  const handlePdfDownload = async () => {
    if (!profileContentRef.current || generatingPdf || !profile) return;

    try {
      setGeneratingPdf(true);

      const canvas = await html2canvas(profileContentRef.current, {
        scale: PDF_RENDER_SCALE,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: profileContentRef.current.scrollWidth,
        windowHeight: profileContentRef.current.scrollHeight,
      });

      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imageWidth = pageWidth;
      const pageHeightPx = Math.floor((canvas.width * pageHeight) / pageWidth);
      let renderedHeight = 0;
      let pageIndex = 0;

      while (renderedHeight < canvas.height) {
        const sliceHeight = Math.min(
          pageHeightPx,
          canvas.height - renderedHeight,
        );
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const pageContext = pageCanvas.getContext('2d');
        if (!pageContext) {
          throw new Error('Failed to create PDF page canvas');
        }

        pageContext.fillStyle = '#ffffff';
        pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageContext.drawImage(
          canvas,
          0,
          renderedHeight,
          canvas.width,
          sliceHeight,
          0,
          0,
          pageCanvas.width,
          pageCanvas.height,
        );

        const pageImageData = pageCanvas.toDataURL(
          'image/jpeg',
          PDF_IMAGE_QUALITY,
        );
        const imageHeight = (sliceHeight * imageWidth) / canvas.width;

        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(
          pageImageData,
          'JPEG',
          0,
          0,
          imageWidth,
          imageHeight,
          undefined,
          'MEDIUM',
        );

        renderedHeight += sliceHeight;
        pageIndex += 1;
      }

      pdf.save(
        `${profile.company_name || 'supplier-profile'}-${new Date().toISOString().slice(0, 10)}.pdf`,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleCsvDownload = () => {
    if (!profile) return;

    const csvContent = buildProfileCsv({
      profile,
      operations,
      countries,
      allCountries,
      policies,
      certifications,
      restrictedFields,
      publishedReports,
      industryOptions,
    });
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const createdDate = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `${profile.company_name || 'supplier-profile'}-${createdDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '80vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!profile)
    return (
      <Container className="py-5">
        <div className="text-center">
          <i className="bi bi-exclamation-triangle-fill text-warning display-4 mb-3"></i>
          <h3 className="mb-2">Profile Not Found</h3>
          <p className="text-muted mb-0">
            The requested supplier profile is not available. It may not exist or
            has not been published yet.
          </p>
        </div>
      </Container>
    );

  return (
    <div className="profile-shell">
      <div className="profile-topbar sticky-top" style={{ zIndex: 1000 }}>
        <Container>
          <div className="d-flex justify-content-end py-2">
            <Dropdown align="end">
              <Dropdown.Toggle
                variant="light"
                id="export-dropdown"
                disabled={generatingPdf}
              >
                <i className="bi bi-download me-2" />
                {generatingPdf ? 'Generating PDF...' : 'Export'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handlePdfDownload}>PDF</Dropdown.Item>
                <Dropdown.Item onClick={handleCsvDownload}>CSV</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Container>
      </div>
      <Container className="pt-4 profile-page" ref={profileContentRef}>
        <Card className="profile-hero-card mb-4">
          <div className="profile-hero-header p-4 p-md-5">
            <Row className="align-items-center g-4">
              <Col md="auto" className="text-center">
                <div className="bg-white border rounded-4 p-2 d-inline-block shadow-sm">
                  {profile.logo_url ? (
                    <img
                      src={profile.logo_url}
                      alt="Logo"
                      style={{ width: 96, height: 96, objectFit: 'contain' }}
                    />
                  ) : (
                    <div
                      className="bg-light rounded d-flex align-items-center justify-content-center"
                      style={{ width: 96, height: 96 }}
                    >
                      <i className="bi bi-building text-secondary fs-2"></i>
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div className="profile-eyebrow mb-3">
                  Public Supplier Profile
                </div>
                <img
                  src={logo}
                  alt="SupplyPassport Logo"
                  height="52"
                  className="mb-4"
                />
                <h2 className="fw-bold mb-1">{profile.company_name}</h2>
                <div
                  className="mb-3"
                  style={{ color: 'rgba(241,245,249,0.82)' }}
                >
                  {industryLabel}
                </div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <span className="profile-meta-badge">
                    <i className="bi bi-geo-alt"></i>
                    {headquartersName}
                  </span>
                  {profile.updated_at && (
                    <span className="profile-meta-badge">
                      <i className="bi bi-clock-history"></i>
                      Updated{' '}
                      {new Date(profile.updated_at).toLocaleDateString()}
                    </span>
                  )}
                  <span className="profile-meta-badge">
                    <i className="bi bi-diagram-3"></i>
                    {countries.length} active market
                    {countries.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div
                  className="d-flex flex-wrap gap-3 small"
                  style={{ color: 'rgba(241,245,249,0.82)' }}
                >
                  {profile.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-decoration-none"
                      style={{ color: 'rgba(241,245,249,0.82)' }}
                    >
                      <i className="bi bi-globe me-1"></i>
                      {profile.website.replace(/(^\w+:|^)\/\//, '')}
                    </a>
                  )}
                  {profile.contact_email && (
                    <span>
                      <i className="bi bi-envelope me-1"></i>
                      {profile.contact_email}
                    </span>
                  )}
                </div>
              </Col>
              <Col md="auto" className="text-md-end">
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-light rounded-pill px-3"
                  >
                    <i className="bi bi-linkedin me-2"></i>LinkedIn
                  </a>
                )}
              </Col>
            </Row>
          </div>
          <Card.Body className="p-4 p-md-5">
            <Row className="g-3">
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Headquarters</div>
                    <div className="profile-metric-value">
                      {headquartersName}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Founded</div>
                    <div className="profile-metric-value">
                      {profile.founded_year || '—'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Employees</div>
                    <div className="profile-metric-value">
                      {operations?.employee_count || 'N/A'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">
                      Published Reports
                    </div>
                    <div className="profile-metric-value">
                      {publishedReports.length}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row>
          <Col lg={8}>
            <section className="mb-4">
              <div className="profile-section-label">About</div>
              <Card className="profile-section-card">
                <div className="profile-section-head">
                  <h4 className="mb-0">Company Overview</h4>
                </div>
                <Card.Body>
                  <p
                    className="text-secondary mb-0 leading-relaxed"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {profile.description ||
                      'The company has not provided a description yet.'}
                  </p>
                </Card.Body>
              </Card>
            </section>

            {profile.display_assesment_results && (
              <section className="mb-4">
                <div className="profile-section-label">
                  Company Compliance Assessments
                </div>
                <Card className="profile-section-card overflow-hidden">
                  <div className="profile-section-head">
                    <h4 className="mb-0">Published Assessment Reports</h4>
                  </div>
                  {publishedReports.length === 0 ? (
                    <Card.Body className="text-center py-4 text-muted small">
                      No published compliance reports available.
                    </Card.Body>
                  ) : (
                    <div className="list-group list-group-flush profile-list-group">
                      {publishedReports.map((report) => {
                        const meta = report.report_data?.meta;
                        const score = getTotalScorePercentage(
                          meta?.total_score,
                          meta?.max_score,
                        );
                        return (
                          <div key={report.id} className="list-group-item p-4">
                            <Row className="align-items-center">
                              <Col md={6}>
                                <div className="fw-bold text-dark mb-1">
                                  {meta?.template_name || 'Assessment Report'}
                                </div>
                                <div className="text-muted x-small">
                                  Published on:{' '}
                                  {new Date(
                                    report.published_at,
                                  ).toLocaleDateString()}
                                </div>
                              </Col>
                              <Col md={3} className="text-center">
                                <Badge
                                  ref={(el) =>
                                    el?.style.setProperty(
                                      'background-color',
                                      getBarColor(score),
                                      'important',
                                    )
                                  }
                                  className="rounded-pill px-3 mb-1"
                                >
                                  {score}% Overall
                                </Badge>
                                <ProgressBar
                                  now={score}
                                  ref={(el) =>
                                    el?.style.setProperty(
                                      'background-color',
                                      getBarColor(score),
                                      'important',
                                    )
                                  }
                                  style={{ height: '4px' }}
                                  className="mt-1"
                                />
                              </Col>
                              <Col md={3} className="text-end mt-3 mt-md-0">
                                {generatingPdf ? (
                                  <span className="text-muted small">
                                    Report link available online
                                  </span>
                                ) : (
                                  <div className="d-grid d-md-block">
                                    <Link
                                      to={`/public/report/${report.id}`}
                                      target="_blank"
                                      className="btn btn-sm btn-outline-primary rounded-pill"
                                    >
                                      View Report{' '}
                                      <i className="bi bi-arrow-right ms-1"></i>
                                    </Link>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </section>
            )}

            {showFinancial && (
              <section className="mb-4">
                <div className="profile-section-label">
                  Financial & Identity
                </div>

                <Card className="profile-section-card overflow-hidden mb-3">
                  <div className="profile-section-head">
                    <h4 className="mb-0">Financial Disclosure</h4>
                  </div>
                  <Card.Body>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">UBO Details</span>
                      <span className="fw-semibold">
                        {restrictedFields?.ubo_details || '—'}
                      </span>
                    </div>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">
                        Sanction Screening
                      </span>
                      <span className="fw-semibold">
                        {sanctionScreeningStatusMap[
                          restrictedFields?.sanction_screening_status ||
                            'not_screened'
                        ]?.label || '—'}
                      </span>
                    </div>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">Credit Score</span>
                      <span className="fw-semibold">
                        {restrictedFields?.credit_score_value || '—'}
                        {' ('}
                        {restrictedFields?.credit_score_provider || '—'}
                        {')'}
                      </span>
                    </div>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">Last Audit Date</span>
                      <span className="fw-semibold">
                        {restrictedFields?.last_financial_audit_date
                          ? new Date(
                              restrictedFields.last_financial_audit_date,
                            ).toLocaleDateString()
                          : '—'}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </section>
            )}

            <section className="mb-4">
              <div className="profile-section-label">
                Supply Chain Transparency
              </div>

              <Card className="profile-section-card overflow-hidden">
                <div className="profile-section-head">
                  <h4 className="mb-0">Supply Chain Visibility</h4>
                </div>
                <Card.Body>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Tier 1 Visibility</span>
                    <span className="fw-semibold">
                      {tierOneSuppliersAssessmentMap[
                        profile.tier_1_visibility_level || 'unknown'
                      ]?.label
                        ? `${
                            tierOneSuppliersAssessmentMap[
                              profile.tier_1_visibility_level || 'unknown'
                            ]?.label
                          }`
                        : ''}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Tier 2 Visibility</span>
                    <span className="fw-semibold">
                      {tierTwoSuppliersAssessmentMap[
                        profile.tier_2_visibility_level || 'none'
                      ]?.label
                        ? `${
                            tierTwoSuppliersAssessmentMap[
                              profile.tier_2_visibility_level || 'none'
                            ]?.label
                          }`
                        : ''}
                    </span>
                  </div>
                  <div className="pt-3">
                    <div className="profile-kv-label mb-2">
                      Raw Material Origins
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {profile.raw_material_origins?.length ? (
                        profile.raw_material_origins.map((code) => {
                          const cntry = allCountries.find(
                            (c) => c.code === code,
                          );
                          return (
                            <Badge
                              key={cntry.id}
                              bg="white"
                              text="dark"
                              className="profile-chip"
                            >
                              {cntry.name || code}
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-muted x-small">—</span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </section>

            <section className="mb-4">
              <div className="profile-section-label">
                Certifications & Accreditations
              </div>
              <Card className="profile-section-card overflow-hidden">
                <div className="profile-section-head">
                  <h4 className="mb-0">Certificates</h4>
                </div>
                {certifications.length === 0 ? (
                  <Card.Body className="text-muted small py-4 text-center">
                    No certifications found.
                  </Card.Body>
                ) : (
                  <div className="list-group list-group-flush profile-list-group">
                    {certifications.map((cert) => (
                      <div
                        key={cert.id}
                        className="list-group-item d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3"
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="icon-square bg-success-subtle text-success">
                            <i className="bi bi-patch-check-fill fs-5"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {cert.certificate_name}
                            </div>
                            <div className="text-muted x-small">
                              {cert.issuing_body} •{' '}
                              {cert.valid_until
                                ? `Expires ${cert.valid_until}`
                                : 'Permanent'}
                            </div>
                          </div>
                        </div>
                        {cert.signed_url && !generatingPdf && (
                          <a
                            href={cert.signed_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light border px-3 rounded-pill mt-1 mt-sm-0"
                          >
                            View Doc{' '}
                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </section>

            <section className="mb-4">
              <div className="profile-section-label">
                Public Compliance Policies
              </div>
              <Card className="profile-section-card overflow-hidden">
                <div className="profile-section-head">
                  <h4 className="mb-0">Policy Library</h4>
                </div>
                {policies.length === 0 ? (
                  <Card.Body className="text-muted small py-4 text-center">
                    No public policies available.
                  </Card.Body>
                ) : (
                  <div className="list-group list-group-flush profile-list-group">
                    {policies.map((policy) => (
                      <div
                        key={policy.id}
                        className="list-group-item d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3"
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="icon-square bg-primary-subtle text-primary">
                            <i className="bi bi-file-earmark-text-fill fs-5"></i>
                          </div>
                          <span className="fw-bold text-dark">
                            {policy.policy_name || policy.policy_type}
                          </span>
                        </div>
                        {policy.signed_url && !generatingPdf && (
                          <a
                            href={policy.signed_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light border px-3 rounded-pill mt-1 mt-sm-0"
                          >
                            View Policy{' '}
                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </section>
          </Col>

          <Col lg={4}>
            <section className="mb-4">
              <div className="profile-section-label">Company Snapshot</div>
              <Card className="profile-section-card">
                <div className="profile-section-head">
                  <h4 className="mb-0">Core Details</h4>
                </div>
                <Card.Body>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Headquarters</span>
                    <span className="fw-semibold">{headquartersName}</span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Founded</span>
                    <span className="fw-semibold">
                      {profile.founded_year || '—'}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Registration</span>
                    <span className="fw-semibold">
                      {profile.registration_number || '—'}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">D-U-N-S</span>
                    <span className="fw-semibold">
                      {profile.duns_number || '—'}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Website</span>
                    {profile.website ? (
                      <a
                        href={profile.website}
                        target="_blank"
                        rel="noreferrer"
                        className="fw-semibold text-decoration-none"
                      >
                        Visit
                        <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                      </a>
                    ) : (
                      <span className="fw-semibold">—</span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </section>

            <section className="mb-4">
              <div className="profile-section-label">Operational Insights</div>
              <Card className="profile-section-card">
                <div className="profile-section-head">
                  <h4 className="mb-0">Operations Snapshot</h4>
                </div>
                <Card.Body>
                  {profile.industry_id && (
                    <div className="mb-4">
                      <label className="profile-metric-label d-block mb-1">
                        Industry
                      </label>
                      <div className="d-flex align-items-center gap-2 fw-bold">
                        {industryLabel}
                      </div>
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="profile-metric-label d-block mb-1">
                      Company Size
                    </label>
                    <div className="d-flex align-items-center gap-2 fw-bold">
                      <i className="bi bi-people text-primary"></i>
                      {operations?.employee_count || 'N/A'} Employees
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="profile-metric-label d-block mb-1">
                      Revenue Range
                    </label>
                    <div className="d-flex align-items-center gap-2 fw-bold">
                      <i className="bi bi-graph-up-arrow text-primary"></i>
                      {operations?.revenue_range || 'N/A'}
                    </div>
                  </div>
                  <hr className="my-4 opacity-50" />
                  <div>
                    <label className="profile-metric-label d-block mb-2">
                      Active Markets
                    </label>
                    <div className="d-flex flex-wrap gap-1">
                      {countries.length > 0 ? (
                        countries.map((c, i) => (
                          <Badge
                            key={i}
                            bg="white"
                            text="dark"
                            className="profile-chip"
                          >
                            {c.countries?.name || c.country_id}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted x-small">
                          No markets specified
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </section>
          </Col>
        </Row>
        <p
          className="text-center text-muted mt-4 mb-0"
          style={{ fontSize: '0.75rem' }}
        >
          This profile is generated based on self-reported data provided by the
          assessed entity. SupplyPassport has not independently verified or
          audited this information and assumes no responsibility for its
          accuracy or completeness. For more information, visit{' '}
          <a href="https://www.supplypassport.co">www.supplypassport.co</a>
        </p>
      </Container>
    </div>
  );
};

export default SupplierProfilePublic;
